import React, { useEffect, useMemo, useRef, useState } from "react";
import { sanitizeEmailHtml } from "../../utils/sanitizeHtml";

const MIN_HEIGHT_PX = 320;

interface EmailPreviewFrameProps {
  html: string;
  title: string;
}

const EmailPreviewFrame: React.FC<EmailPreviewFrameProps> = ({
  html,
  title,
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(MIN_HEIGHT_PX);
  const document = useMemo(() => sanitizeEmailHtml(html), [html]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    let observer: ResizeObserver | undefined;
    const fit = () => {
      const page = frame.contentDocument?.documentElement;
      if (page) setHeight(Math.max(MIN_HEIGHT_PX, page.scrollHeight));
    };
    const onLoad = () => {
      fit();
      const body = frame.contentDocument?.body;
      if (body && typeof ResizeObserver !== "undefined") {
        observer?.disconnect();
        observer = new ResizeObserver(fit);
        observer.observe(body);
      }
    };
    frame.addEventListener("load", onLoad);
    return () => {
      frame.removeEventListener("load", onLoad);
      observer?.disconnect();
    };
  }, [document]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      // Same origin lets the page measure the e-mail; without allow-scripts nothing in it can run.
      sandbox="allow-same-origin"
      srcDoc={document}
      translate="no"
      className="block w-full border-0"
      style={{ height }}
    />
  );
};

export default EmailPreviewFrame;
